import { segundosDeBd } from "./conciliacionPagos";
import { AFILIACION_EN_LINEA, AFILIACION_PINPAD, PLANTEL_DE_AFILIACION } from "./archivosBanco";

// Cruce de los movimientos del banco (guardados en tblmultipagos_banco_movimientos) contra las ordenes de
// Multipagos. El banco es la referencia: dice cuanto dinero entro a cada plantel. Multipagos dice que registro cada
// plantel. Cada diferencia es un "caso" con lo que el plantel debe corregir. SOLO LEE: no cambia nada.
// Las reglas de TPV son las del analisis de octubre 2026 (correccion_bd/2026-10-20_tpv_octubre/cruce.py) y las de
// ventanilla las del cruce de ventanilla de esa misma fecha.

const MIN = 60;
const HORA = 3600;
const DIA = 86400;
const igual = (a, b) => Math.abs(a - b) < 0.005;
const redondear = (n) => Math.round(n * 100) / 100;
const oficialDeBd = (idBd) => (idBd === "" || idBd === null || idBd === undefined ? "" : String(Number(idBd) - 2).padStart(2, "0"));
const bdDeOficial = (oficial) => (oficial ? String(Number(oficial) + 2) : "");

export const ETIQUETAS_FUENTE = { TARJETA: "Tarjeta", BANORTE: "Ventanilla Banorte", BBVA: "Ventanilla BBVA" };

// Metodo con el que el plantel valido la orden (id_origen / id_banco de tblmultipagos_intenciones)
export function metodoDeOrden(o) {
  const origen = String(o.id_origen || "");
  if (origen === "3") return "TPV";
  if (origen === "4") return "LINEA";
  if (origen === "2") return "PINPAD";
  if (origen === "1") return String(o.id_banco) === "2" ? "BBVA" : "BANORTE";
  return "";
}
const FUENTE_DE_METODO = { TPV: "TARJETA", LINEA: "TARJETA", PINPAD: "TARJETA", BANORTE: "BANORTE", BBVA: "BBVA" };
const METODOS_TARJETA = ["TPV", "LINEA", "PINPAD"];
export const ETIQUETAS_METODO = { TPV: "TPV", LINEA: "pago en línea", PINPAD: "Pinpad", BANORTE: "ventanilla Banorte", BBVA: "ventanilla BBVA" };

// Tipos de caso, en el orden en que se muestran. "quien": quien lo corrige.
export const TIPOS_CASO = {
  VALIDADO_SIN_COBRO: { titulo: "Validado sin cobro en el banco", quien: "PLANTEL" },
  DOBLE_REGISTRO: { titulo: "Mismo cobro en dos órdenes", quien: "PLANTEL" },
  COBRO_DEVUELTO: { titulo: "Validado, pero el cobro se devolvió", quien: "PLANTEL" },
  COBRADO_SIN_VALIDAR: { titulo: "Cobrado y sin validar", quien: "PLANTEL" },
  SIN_ORDEN: { titulo: "Cobro sin orden en Multipagos", quien: "PLANTEL" },
  DATO_MAL_CAPTURADO: { titulo: "Autorización o folio mal capturado", quien: "PLANTEL" },
  FOLIO_DE_OTRO_ALUMNO: { titulo: "Se tecleó el folio de otro alumno", quien: "PLANTEL" },
  METODO_EQUIVOCADO: { titulo: "Validado con otro método", quien: "PLANTEL" },
  BANCO_EQUIVOCADO: { titulo: "Validado con el otro banco", quien: "PLANTEL" },
  MONTO_DISTINTO: { titulo: "Importe distinto al de la orden", quien: "PLANTEL" },
  UN_COBRO_VARIAS_ORDENES: { titulo: "Un cobro para varias órdenes", quien: "PLANTEL" },
  VARIOS_COBROS_UNA_ORDEN: { titulo: "Orden pagada en varias partes", quien: "PLANTEL" },
  OTRO_PLANTEL: { titulo: "Validado en una orden de otro plantel", quien: "PLANTEL" },
  POSIBLE_COBRO_DOBLE: { titulo: "Posible cobro doble", quien: "PLANTEL" },
  ORDEN_CANCELADA: { titulo: "Pagó una orden cancelada", quien: "PLANTEL" },
  EN_LINEA: { titulo: "Pago en línea sin registro correcto", quien: "SISTEMAS" },
};

const dinero = (n) => `$${Number(n).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const ddmm = (iso) => (iso ? `${String(iso).substring(8, 10)}/${String(iso).substring(5, 7)}` : "");

// La referencia BBVA del sistema anterior: plantel(2) + concepto(2) + matricula sin su 8o digito (13) + 3 digitos.
// El 8o digito de la matricula es el primero del plantel, que es el primero de la referencia.
// Si la referencia no tiene ese formato, lo que sale no es una matricula: se regresa "".
export const matriculaDeReferenciaBbva = (ref) => {
  if (!/^\d{20}$/.test(ref)) return "";
  const mat = ref.substring(4, 11) + ref.charAt(0) + ref.substring(11, 17);
  return matriculaValida(mat) ? mat : "";
};

// Forma de una matricula: generacion (2) + 414 + 9 digitos, o la ficticia de externos y ex-alumnos (999 + 11)
export const matriculaValida = (mat) => /^(\d{2}414\d{9}|999\d{11})$/.test(String(mat || ""));

// Lo que el cajero tecleo en la TPV, en palabras: "0135" no le dice nada
export function explicarTecleado(t) {
  const s = String(t || "").trim();
  if (!s) return "";
  if (/^\d{4}$/.test(s)) return `plantel ${s.substring(0, 2)}, concepto ${s.substring(2, 4)}: solo el inicio de una referencia, no dice el alumno`;
  if (/^\d{6}$/.test(s)) return "parece un folio de orden, pero no es de ninguna orden del plantel";
  if (matriculaValida(s)) return "es una matrícula";
  if (/^\d{20,}$/.test(s)) return "es una referencia";
  return "";
}

// Como se cobro, en palabras del cajero ("Tarjeta" no le dice nada)
export function etiquetaMedio(fuente, cuenta = "") {
  if (fuente === "BANORTE") return "Ventanilla Banorte";
  if (fuente === "BBVA") return "Ventanilla BBVA";
  if (cuenta === AFILIACION_EN_LINEA) return "Pago en línea";
  if (cuenta === AFILIACION_PINPAD) return "Pinpad";
  if (PLANTEL_DE_AFILIACION[cuenta]) return "TPV del plantel";
  return "Tarjeta";
}
const ETIQUETA_DE_METODO = { TPV: "TPV del plantel", LINEA: "Pago en línea", PINPAD: "Pinpad", BANORTE: "Ventanilla Banorte", BBVA: "Ventanilla BBVA" };

// De quien es un cobro sin orden, segun lo que trae la referencia: { matricula, conceptos } ("" y [] si no se sabe).
//   Banorte (34) y pago en linea (30): plantel(2) + conceptos(10, de 2 en 2) + matricula(14) + ...
//   BBVA del sistema anterior (20): plantel(2) + concepto(2) + matricula sin su 8o digito(13) + dv(3)
//   Lo tecleado en la TPV: la matricula sola (14) o una referencia de 20.
export function datosDeReferencia(fuente, referencia, tecleado = "") {
  const conceptosDe = (bloque) => (bloque.match(/\d{2}/g) || []).filter((c) => c !== "00");
  const ref = String(referencia || "").trim();
  if ((fuente === "BANORTE" || fuente === "TARJETA") && /^\d{30}(\d{4})?$/.test(ref) && matriculaValida(ref.substring(12, 26))) {
    return { matricula: ref.substring(12, 26), conceptos: conceptosDe(ref.substring(2, 12)) };
  }
  const viejaBbva = (r) => (matriculaDeReferenciaBbva(r) ? { matricula: matriculaDeReferenciaBbva(r), conceptos: conceptosDe(r.substring(2, 4)) } : null);
  if (fuente === "BBVA") return viejaBbva(ref) || { matricula: "", conceptos: [] };
  const t = String(tecleado || "").trim();
  if (matriculaValida(t)) return { matricula: t, conceptos: [] };
  return viejaBbva(t) || { matricula: "", conceptos: [] };
}

// Por que la autorizacion capturada no es la del banco (lo que mas se vio en octubre)
function causaDeCaptura(o, m) {
  const c = String(o.folio_banco || "").trim();
  if (!c) return "no se capturó la autorización";
  if (m.cuenta && c.replace(/^0+/, "") === m.cuenta.replace(/^0+/, "")) return "se capturó el número de afiliación de la terminal en lugar de la autorización del ticket";
  if (o.folio_registro && c === o.folio_registro) return "se capturó el folio de la orden en lugar de la autorización del ticket";
  if (!/^[A-Za-z0-9]{6}$/.test(c)) return `lo capturado tiene ${c.length} caracteres; la autorización del ticket tiene 6`;
  return "";
}

// El cobro es de esa orden: lo capturado es la autorizacion o el folio del banco, o el alumno pago en ventanilla con la
// referencia que Multipagos genero para la orden (o, en BBVA, con la que el cajero capturo como referencia pagada)
function cobroDeLaOrden(m, o) {
  const dato = String((m.fuente === "TARJETA" ? m.autorizacion : m.folio) || "").trim().toUpperCase();
  if (dato && o.folio_banco.toUpperCase() === dato) return true;
  const ref = String(m.referencia || "").trim();
  if (!ref || m.fuente === "TARJETA") return false;
  if (m.fuente === "BANORTE") return ref === String(o.referencia_banorte || "").trim();
  return ref === String(o.referencia_bbva || "").trim() || ref === String(o.referencia_pagada || "").trim();
}

// Lo que el cajero tecleo en la TPV como referencia -> matriculas posibles
function matriculasDeTecleado(r2) {
  if (!/^\d{14,}$/.test(r2)) return [];
  if (r2.length === 14) return [r2];
  const mats = [r2.substring(4, 18)];
  if (r2.length === 20) mats.push(matriculaDeReferenciaBbva(r2));
  return mats;
}

const aOrden = (o) => {
  const monto = Number(o.importe_pagado) > 0 ? Number(o.importe_pagado) : Number(o.importe_total);
  return {
    ...o,
    id: String(o.id),
    pl: String(o.id_plantel || ""),
    monto: Number.isFinite(monto) ? monto : 0,
    metodo: metodoDeOrden(o),
    validada: o.estatus === "PAGADO" || o.estatus === "PARCIAL",
    folio_banco: String(o.folio_banco || "").trim(),
    folio_registro: String(o.folio_registro || "").trim(),
    tsVal: segundosDeBd(o.fecha_validacion, o.hora_validacion),
    tsCre: segundosDeBd(o.fecha_creacion, o.hora_creacion),
    // Externos y ex-alumnos comparten una matricula ficticia por plantel y concepto: varias personas tienen la
    // misma referencia, asi que la referencia no dice quien pago
    ficticia: String(o.codigo_alumno || "").trim().startsWith("999") || String(o.exalumno || "").trim() !== "",
    confirmada: String(o.fecha_pago || "").trim() !== "", // el banco ya confirmo este pago (validacion con el banco)
  };
};

const aMovimiento = (m) => ({
  ...m,
  pl: String(m.id_plantel || ""),
  importe: Number(m.importe),
  comision: Number(m.comision || 0),
  iva: Number(m.iva || 0),
  depositado: Number(m.depositado),
  ts: segundosDeBd(m.fecha, m.hora || "00:00:00"),
  autorizacion: String(m.autorizacion || "").trim(),
  referencia: String(m.referencia || "").trim(),
  referencia_tecleada: String(m.referencia_tecleada || "").trim(),
  folio: String(m.folio || "").trim(),
});

const agrupar = (lista, clave) => {
  const mapa = new Map();
  lista.forEach((x) => {
    const k = clave(x);
    if (!k) return;
    if (!mapa.has(k)) mapa.set(k, []);
    mapa.get(k).push(x);
  });
  return mapa;
};

// ================================================================ analisis
// movimientos: filas de tblmultipagos_banco_movimientos; ordenes: filas de tblmultipagos_intenciones;
// cajeros: { id: { nombre, id_plantel } }; cobertura: { FUENTE: { desde, hasta } } de lo guardado;
// periodo: { inicio, fin } (AAAA-MM-DD). Los movimientos y las ordenes pueden traer dias fuera del periodo: se usan
// para ligar, pero solo se reporta lo del periodo.
export function analizarBancos({ movimientos = [], ordenes = [], cajeros = {}, cobertura = {}, periodo, nombresPlanteles = {} }) {
  const ords = ordenes.map(aOrden);
  const movs = movimientos.map(aMovimiento).filter((m) => m.ts !== null).sort((a, b) => a.ts - b.ts);
  const enPeriodo = (fecha) => fecha >= periodo.inicio && fecha <= periodo.fin;
  const usado = new Map(); // id de orden -> movimiento que la explica
  const resultados = []; // { m, clase, o, nota, probable }
  const anota = (m, clase, o = null, nota = "", probable = false) => {
    if (o) usado.set(o.id, m);
    const r = { m, clase, o, nota, probable };
    resultados.push(r);
    return r;
  };
  const libre = (o) => !usado.has(o.id);
  const mejor = (cands, m) => [...cands].sort((a, b) =>
    (igual(a.monto, m.importe) ? 0 : 1) - (igual(b.monto, m.importe) ? 0 : 1)
    || (a.tsVal === null ? 1 : 0) - (b.tsVal === null ? 1 : 0)
    || Math.abs((a.tsVal || m.ts) - m.ts) - Math.abs((b.tsVal || m.ts) - m.ts))[0];

  const validadas = ords.filter((o) => o.validada);
  const porFolioBanco = agrupar(validadas, (o) => o.folio_banco.toUpperCase());
  const porFolioRegistro = agrupar(ords, (o) => o.folio_registro && `${o.pl}|${o.folio_registro}`);
  const porMatricula = agrupar(ords, (o) => o.codigo_alumno && `${o.pl}|${String(o.codigo_alumno).trim()}`);
  const porMatriculaSinPlantel = agrupar(ords, (o) => String(o.codigo_alumno || "").trim());
  const porRefBbva = agrupar(ords, (o) => String(o.referencia_bbva || "").trim());
  const porRefBanorte = agrupar(ords, (o) => String(o.referencia_banorte || "").trim());
  const porRefPagada = agrupar(validadas, (o) => String(o.referencia_pagada || "").trim());
  const tpvValidadas = validadas.filter((o) => o.metodo === "TPV");

  const ventasTpv = movs.filter((m) => m.fuente === "TARJETA" && m.tipo === "VENTA" && PLANTEL_DE_AFILIACION[m.cuenta]);
  const ventasLinea = movs.filter((m) => m.fuente === "TARJETA" && m.tipo === "VENTA" && m.cuenta === AFILIACION_EN_LINEA);
  const devoluciones = movs.filter((m) => m.fuente === "TARJETA" && m.tipo === "DEVOLUCION");
  const pinpad = movs.filter((m) => m.fuente === "TARJETA" && m.cuenta === AFILIACION_PINPAD);
  const devueltas = new Set(devoluciones.map((d) => `${d.cuenta}|${d.folio}`).filter((k) => !k.endsWith("|")));

  // ------------------------------------------------------------ TPV de los planteles
  const restantesTpv = [];
  const pendientesTpv = [];
  ventasTpv.forEach((m) => {
    // 1. misma autorizacion, mismo plantel
    const c = (porFolioBanco.get(m.autorizacion.toUpperCase()) || []).filter((o) => o.metodo === "TPV" && libre(o) && o.pl === m.pl);
    if (c.length) {
      const o = mejor(c, m);
      anota(m, igual(o.monto, m.importe) ? "OK" : "MONTO_DISTINTO", o);
      return;
    }
    pendientesTpv.push(m);
  });
  pendientesTpv.forEach((m) => {
    const conAut = (porFolioBanco.get(m.autorizacion.toUpperCase()) || []).filter(libre);
    // 2. misma autorizacion y monto en una validacion TPV de OTRO plantel
    const otro = conAut.filter((o) => o.metodo === "TPV" && igual(o.monto, m.importe));
    if (otro.length) { anota(m, "OTRO_PLANTEL", mejor(otro, m)); return; }
    // 2b. la autorizacion de la TPV quedo capturada como folio de una validacion de ventanilla o en linea
    const otroMetodo = conAut.filter((o) => o.metodo !== "TPV" && o.pl === m.pl);
    if (otroMetodo.length) { anota(m, "METODO_EQUIVOCADO", mejor(otroMetodo, m), "la autorización de la TPV quedó capturada en una validación de otro método"); return; }
    // 3. por lo que tecleo el cajero en la terminal: folio de la orden, referencia BBVA de la orden o la matricula
    const r2 = m.referencia_tecleada;
    let cands = [];
    let como = "";
    if (r2) {
      cands = porFolioRegistro.get(`${m.pl}|${r2}`) || [];
      como = "folio";
      if (!cands.length) { cands = (porRefBbva.get(r2) || []).filter((o) => o.pl === m.pl); como = "referencia BBVA"; }
      if (!cands.length) {
        cands = matriculasDeTecleado(r2).flatMap((mat) => porMatricula.get(`${m.pl}|${mat}`) || [])
          .filter((o) => igual(o.monto, m.importe) && o.metodo !== "LINEA");
        como = "matrícula";
      }
    }
    const libres = cands.filter(libre);
    if (libres.length) {
      const o = mejor(libres, m);
      const nota = `se encontró por ${{ folio: "el folio tecleado", "referencia BBVA": "la referencia BBVA tecleada", matrícula: "la matrícula tecleada" }[como]} en la terminal`;
      if (o.estatus === "PENDIENTE") {
        const r = anota(m, "COBRADO_SIN_VALIDAR", o, nota);
        // Se valida con el banco solo si el folio tecleado es de una sola orden del plantel por ese importe (el folio se
        // generaba al azar y puede estar en dos ordenes; sin contar las canceladas). Igual que el servidor.
        const delImporte = cands.filter((x) => x.estatus !== "CANCELADO" && igual(x.monto, m.importe));
        r.automatico = como === "folio" && delImporte.length === 1 && delImporte[0] === o && !o.ficticia;
      } else if (o.metodo === "TPV") anota(m, "DATO_MAL_CAPTURADO", o, nota);
      else if (o.validada) anota(m, "METODO_EQUIVOCADO", o, nota);
      else if (o.estatus === "CANCELADO") anota(m, "ORDEN_CANCELADA", o, nota);
      else anota(m, "SIN_ORDEN", null, `${nota}, pero la orden ${o.id} está ${o.estatus}`);
      return;
    }
    if (cands.length) {
      const previo = usado.get(cands[0].id); // el cobro que ya explica esa orden, para mostrarlo al cajero
      anota(m, "POSIBLE_COBRO_DOBLE", cands[0], `la orden (encontrada por ${como}) ya tiene otro cobro`).otroCobro = previo || null;
      return;
    }
    restantesTpv.push(m);
  });
  // Las ligas "probables" van al final, para no quitarle a otro cobro la orden que le corresponde por folio
  restantesTpv.forEach((m) => {
    // 4. una validacion TPV del plantel por el mismo monto, hecha entre 5 min antes y 60 min despues del cobro
    const c = tpvValidadas.filter((o) => libre(o) && o.pl === m.pl && igual(o.monto, m.importe) && o.tsVal !== null
      && o.tsVal - m.ts >= -5 * MIN && o.tsVal - m.ts <= HORA);
    if (c.length) {
      const o = c.reduce((a, b) => (Math.abs(a.tsVal - m.ts) <= Math.abs(b.tsVal - m.ts) ? a : b));
      anota(m, "DATO_MAL_CAPTURADO", o, "ligada por hora y monto (no por autorización ni folio)", true);
      return;
    }
    // 5. una orden PENDIENTE del plantel por el mismo monto, creada entre 2 h antes y 10 min despues del cobro
    const p = ords.filter((o) => libre(o) && o.estatus === "PENDIENTE" && o.pl === m.pl && igual(o.monto, m.importe) && o.tsCre !== null
      && m.ts - o.tsCre >= -10 * MIN && m.ts - o.tsCre <= 2 * HORA);
    if (p.length) {
      const o = p.reduce((a, b) => (Math.abs(m.ts - a.tsCre) <= Math.abs(m.ts - b.tsCre) ? a : b));
      anota(m, "COBRADO_SIN_VALIDAR", o, "ligada por hora y monto (no por folio)", true);
      return;
    }
    const r2 = m.referencia_tecleada;
    let motivo = `lo tecleado en la terminal (${r2}) no corresponde a ningún folio del plantel`;
    if (!r2) motivo = "no se tecleó nada en la terminal";
    else if (/^\d{14,}$/.test(r2)) motivo = `se tecleó una referencia de ${r2.length} dígitos; el alumno no tiene orden por ese importe en el plantel`;
    anota(m, "SIN_ORDEN", null, motivo);
  });
  // 6. validaciones TPV cuya "autorizacion" es el propio folio (error conocido), hechas hasta 15 dias despues de un
  //    cobro sin orden del mismo plantel e importe
  tpvValidadas.filter((o) => libre(o) && o.tsVal !== null && o.folio_registro && o.folio_banco === o.folio_registro)
    .sort((a, b) => a.tsVal - b.tsVal)
    .forEach((o) => {
      const c = resultados.filter((r) => r.clase === "SIN_ORDEN" && r.m.fuente === "TARJETA" && r.m.pl === o.pl && igual(r.m.importe, o.monto)
        && o.tsVal - r.m.ts >= 0 && o.tsVal - r.m.ts <= 15 * DIA);
      if (!c.length) return;
      const r = c.reduce((a, b) => (o.tsVal - a.m.ts <= o.tsVal - b.m.ts ? a : b));
      Object.assign(r, { clase: "DATO_MAL_CAPTURADO", o, nota: "validada días después del cobro; se capturó el folio como autorización", probable: true });
      usado.set(o.id, r.m);
    });

  // ------------------------------------------------------------ pagos en linea (los registra el sistema, no el plantel)
  ventasLinea.forEach((m) => {
    const conAut = (porFolioBanco.get(m.autorizacion.toUpperCase()) || []).filter(libre);
    const linea = conAut.filter((o) => o.metodo === "LINEA");
    if (linea.length) {
      const o = mejor(linea, m);
      anota(m, igual(o.monto, m.importe) ? "OK" : "EN_LINEA", o, igual(o.monto, m.importe) ? "" : "el importe no coincide con el registro");
      return;
    }
    // el plantel valido el pago en linea del alumno como si fuera de ventanilla o TPV
    const otro = conAut.filter((o) => o.metodo !== "LINEA");
    if (otro.length) {
      anota(m, "METODO_EQUIVOCADO", mejor(otro, m), "el alumno pagó en línea y el plantel lo validó con otro método");
      return;
    }
    const porRef = (porRefBanorte.get(m.referencia) || []).filter(libre);
    if (porRef.length) {
      anota(m, "EN_LINEA", mejor(porRef, m), "el banco aprobó el pago en línea y el registro no quedó PAGADO con esta autorización");
      return;
    }
    anota(m, "EN_LINEA", null, "el banco aprobó el pago en línea y no hay registro con esa referencia");
  });

  // ------------------------------------------------------------ ventanilla (Banorte y BBVA)
  const ventanilla = movs.filter((m) => (m.fuente === "BANORTE" || m.fuente === "BBVA") && m.tipo === "PAGO");
  ventanilla.forEach((m) => {
    const esBbva = m.fuente === "BBVA";
    const metodoBanco = esBbva ? "BBVA" : "BANORTE";
    const directas = esBbva
      ? [...(porRefBbva.get(m.referencia) || []), ...(porRefPagada.get(m.referencia) || [])]
      : [...(porRefBanorte.get(m.referencia) || [])];
    const porFolio = (porFolioBanco.get(m.folio.toUpperCase()) || []).filter((o) => !esBbva || o.metodo === "BBVA");
    const mat = esBbva ? matriculaDeReferenciaBbva(m.referencia) : "";
    const porMat = mat ? (porMatriculaSinPlantel.get(mat) || []).filter((o) => igual(o.monto, m.importe)) : [];
    // Externos y ex-alumnos (matricula ficticia): la referencia es la misma para varias personas, asi que por si sola
    // no liga el deposito con una orden que se creo despues del pago, ni con una que el plantel valido como cobro con
    // tarjeta (TPV, en linea o Pinpad: esa persona pago con tarjeta, no en ventanilla). Si lo capturado es el folio del
    // deposito (porFolio), si se liga.
    const conFolio = new Set(porFolio);
    const puedeSer = (o) => conFolio.has(o) || !o.ficticia
      || ((o.tsCre === null || o.tsCre <= m.ts) && !(o.validada && METODOS_TARJETA.includes(o.metodo)));
    const cands = [...new Set([...directas, ...porFolio])].filter(puedeSer);
    const soloPorMatricula = !cands.length && porMat.length > 0;
    const todas = soloPorMatricula ? porMat : cands;
    const libres = todas.filter(libre);
    const nota = soloPorMatricula ? "ligada por la matrícula que trae la referencia y el importe" : "";
    const elegir = (lista) => (lista.length ? mejor(lista, m) : null);
    const delBanco = elegir(libres.filter((o) => o.validada && o.metodo === metodoBanco));
    const otroBanco = elegir(libres.filter((o) => o.validada && (o.metodo === "BANORTE" || o.metodo === "BBVA")));
    const otroMetodo = elegir(libres.filter((o) => o.validada));
    const pendiente = elegir(libres.filter((o) => o.estatus === "PENDIENTE"));
    const cancelada = elegir(libres.filter((o) => o.estatus === "CANCELADO"));
    if (delBanco) anota(m, igual(delBanco.monto, m.importe) ? "OK" : "MONTO_DISTINTO", delBanco, nota, soloPorMatricula);
    else if (otroBanco) anota(m, "BANCO_EQUIVOCADO", otroBanco, nota, soloPorMatricula);
    else if (otroMetodo) anota(m, "METODO_EQUIVOCADO", otroMetodo, nota, soloPorMatricula);
    else if (pendiente) {
      const r = anota(m, "COBRADO_SIN_VALIDAR", pendiente, nota, soloPorMatricula);
      // Coincidencia exacta: la referencia es la que Multipagos genero para UNA sola orden pendiente por ese importe,
      // del mismo plantel, y ninguna orden con esa referencia esta ya validada (seria un posible cobro doble)
      const exactas = (esBbva ? porRefBbva.get(m.referencia) : porRefBanorte.get(m.referencia)) || [];
      const pendientesIguales = exactas.filter((o) => o.estatus === "PENDIENTE" && igual(o.monto, m.importe));
      const yaValidada = exactas.some((o) => o.validada);
      r.automatico = !soloPorMatricula && exactas.includes(pendiente) && pendientesIguales.length === 1 && !yaValidada
        && igual(pendiente.monto, m.importe) && pendiente.pl === m.pl && !pendiente.ficticia;
      if (!yaValidada && pendientesIguales.length > 1 && !pendiente.ficticia) {
        r.duplicadas = pendientesIguales.map((o) => o.id);
        r.nota = [r.nota, `la referencia tiene ${pendientesIguales.length} órdenes pendientes iguales (la ficha se generó varias veces)`].filter(Boolean).join("; ");
      }
    }
    else if (cancelada) anota(m, "ORDEN_CANCELADA", cancelada, nota, soloPorMatricula);
    else if (todas.length) {
      const previo = usado.get(todas[0].id);
      anota(m, "POSIBLE_COBRO_DOBLE", todas[0], "la referencia ya se pagó antes y la orden ya tiene ese otro pago", soloPorMatricula).otroCobro = previo || null;
    }
    else {
      let motivo = "la referencia no es de ninguna orden de Multipagos";
      if (esBbva && mat && (porMatriculaSinPlantel.get(mat) || []).length) motivo = "la referencia trae una matrícula con órdenes, pero ninguna por este importe";
      if (esBbva && m.cuenta && m.cuenta !== "1806998") motivo += ` (convenio ${m.cuenta})`;
      anota(m, "SIN_ORDEN", null, motivo);
    }
  });

  // ------------------------------------------------------------ pagos agrupados
  // Un solo cobro que pago varias ordenes del mismo alumno (dos conceptos en una sola pasada de la TPV), o una orden
  // que el alumno pago en varias fichas. Sin esto cada parte sale como un caso que contradice al otro: una orden
  // "validada sin cobro" que el cajero desharia, o un "cobro sin orden" que pide generar otra orden.
  const matriculaDe = (o) => String(o.codigo_alumno || "").trim();
  // 1, 2 o 3 elementos de la lista cuyo importe suma el objetivo (o null)
  const subconjunto = (lista, objetivo, valor) => {
    const l = lista.slice(0, 12);
    for (let i = 0; i < l.length; i += 1) {
      if (igual(valor(l[i]), objetivo)) return [l[i]];
      for (let j = i + 1; j < l.length; j += 1) {
        if (igual(valor(l[i]) + valor(l[j]), objetivo)) return [l[i], l[j]];
        for (let k = j + 1; k < l.length; k += 1) if (igual(valor(l[i]) + valor(l[j]) + valor(l[k]), objetivo)) return [l[i], l[j], l[k]];
      }
    }
    return null;
  };
  resultados.forEach((r) => {
    const { m, o } = r;
    if (!o || m.fuente !== "TARJETA" || m.tipo !== "VENTA" || !["DATO_MAL_CAPTURADO", "MONTO_DISTINTO", "COBRADO_SIN_VALIDAR"].includes(r.clase)) return;
    if (m.importe - o.monto < 0.005 || o.ficticia || !matriculaDe(o)) return;
    // las otras ordenes del alumno en el plantel: validadas despues del cobro, o pendientes creadas antes
    const otras = ords.filter((x) => x.id !== o.id && libre(x) && x.pl === o.pl && matriculaDe(x) === matriculaDe(o) && (
      (x.validada && x.tsVal !== null && x.tsVal - m.ts >= -5 * MIN && x.tsVal - m.ts <= 15 * DIA)
      || (x.estatus === "PENDIENTE" && x.tsCre !== null && x.tsCre <= m.ts + DIA && m.ts - x.tsCre <= 30 * DIA)));
    const extra = subconjunto(otras, m.importe - o.monto, (x) => x.monto);
    if (!extra) return;
    extra.forEach((x) => usado.set(x.id, m));
    Object.assign(r, { clase: "UN_COBRO_VARIAS_ORDENES", otras: extra, probable: false, automatico: false,
      nota: `un solo cobro de ${dinero(m.importe)} pagó ${extra.length + 1} órdenes del alumno` });
  });
  resultados.forEach((r) => {
    const { m, o } = r;
    if (!o || r.clase !== "MONTO_DISTINTO" || o.monto - m.importe < 0.005 || o.ficticia || !matriculaDe(o)) return;
    // cobros sin orden del mismo alumno (segun su referencia) en el mismo plantel, hasta 15 dias antes o despues
    const sueltos = resultados.filter((x) => x !== r && x.clase === "SIN_ORDEN" && !x.o && (!x.m.pl || !m.pl || x.m.pl === m.pl)
      && Math.abs(x.m.ts - m.ts) <= 15 * DIA && datosDeReferencia(x.m.fuente, x.m.referencia, x.m.referencia_tecleada).matricula === matriculaDe(o));
    const partes = subconjunto(sueltos, o.monto - m.importe, (x) => x.m.importe);
    if (!partes) return;
    partes.forEach((x) => { x.clase = "PARTE"; }); // ya no es un caso aparte: es parte de este
    Object.assign(r, { clase: "VARIOS_COBROS_UNA_ORDEN", partes: partes.map((x) => x.m), probable: false,
      nota: `el alumno pagó la orden en ${partes.length + 1} partes que juntas suman ${dinero(o.monto)}` });
  });

  // un cobro de TPV que se devolvio al alumno y cuya orden sigue validada
  resultados.forEach((r) => {
    if (r.m.fuente === "TARJETA" && r.m.tipo === "VENTA" && r.o && r.o.validada && devueltas.has(`${r.m.cuenta}|${r.m.folio}`)) {
      r.clase = "COBRO_DEVUELTO";
      r.nota = "el banco devolvió este cobro al alumno";
    }
  });
  // Una orden pagada en varias partes del mismo alumno y del mismo plantel que juntas suman su importe: si uno de los
  // cobros es de la orden (lo capturado es su dato del banco, o es la referencia que se genero para la orden), el banco
  // confirma la orden y no se manda caso al plantel (ya confirmada, deja de ser caso). El servidor vuelve a revisar.
  resultados.forEach((r) => {
    if (r.clase !== "VARIOS_COBROS_UNA_ORDEN") return;
    const { m, o } = r;
    r.seConfirma = !!m.clave && m.pl === o.pl && r.partes.every((p) => p.clave && p.pl === o.pl) && cobroDeLaOrden(m, o);
    if (r.seConfirma && o.confirmada) r.clase = "OK";
  });
  // Se valido la orden con la autorizacion de la TPV, pero en la terminal se tecleo el folio de la orden PENDIENTE de
  // otro alumno del plantel, por el mismo importe y creada antes del cobro: puede que el pago sea del otro alumno y se
  // haya validado la orden equivocada (o solo se tecleo mal el folio). No cuentan los folios repetidos (el mismo folio
  // en dos ordenes del plantel) ni las ordenes creadas despues del cobro.
  resultados.forEach((r) => {
    const { m, o } = r;
    if (r.clase !== "OK" || !o || m.fuente !== "TARJETA" || o.metodo !== "TPV" || o.ficticia || !matriculaDe(o)) return;
    const tecleado = String(m.referencia_tecleada || "").trim();
    if (!/^\d{6}$/.test(tecleado) || tecleado === o.folio_registro) return;
    const delFolio = porFolioRegistro.get(`${m.pl}|${tecleado}`) || [];
    const otra = delFolio.filter((x) => x.estatus === "PENDIENTE" && !x.ficticia && libre(x) && matriculaDe(x) && matriculaDe(x) !== matriculaDe(o)
      && x.tsCre !== null && x.tsCre <= m.ts && igual(x.monto, m.importe));
    if (delFolio.length !== 1 || otra.length !== 1) return;
    Object.assign(r, { clase: "FOLIO_DE_OTRO_ALUMNO", otras: otra,
      nota: `en la terminal se tecleó el folio ${tecleado}, que es de la orden ${otra[0].id} de otro alumno (matrícula ${matriculaDe(otra[0])}) y sigue pendiente` });
  });
  // la misma orden con dos cobros pendientes de validar
  const cuentaPendientes = new Map();
  resultados.filter((r) => r.clase === "COBRADO_SIN_VALIDAR").forEach((r) => cuentaPendientes.set(r.o.id, (cuentaPendientes.get(r.o.id) || 0) + 1));
  resultados.forEach((r) => {
    if (r.clase === "COBRADO_SIN_VALIDAR" && cuentaPendientes.get(r.o.id) > 1) r.nota = [r.nota, "la orden tiene más de un cobro (posible cobro doble)"].filter(Boolean).join("; ");
    // lo que cambio de caso despues, o una orden con dos cobros, ya no se valida sola
    if (r.automatico && (r.clase !== "COBRADO_SIN_VALIDAR" || cuentaPendientes.get(r.o.id) > 1)) r.automatico = false;
  });

  // ------------------------------------------------------------ validaciones sin movimiento en el banco
  const datoDeMovimiento = new Map(); // autorizacion o folio del banco ya usado -> { o: orden que lo tiene, m: movimiento }
  resultados.forEach((r) => {
    if (!r.o) return;
    [r.m.autorizacion, r.m.folio].filter(Boolean).forEach((d) => datoDeMovimiento.set(d.toUpperCase(), { o: r.o, m: r.m }));
  });
  const sinBanco = [];
  let porConfirmar = 0;
  validadas.filter((o) => libre(o) && o.metodo && o.metodo !== "PINPAD" && o.fecha_validacion && enPeriodo(o.fecha_validacion)).forEach((o) => {
    const otra = datoDeMovimiento.get(o.folio_banco.toUpperCase());
    if (otra && otra.o.id !== o.id) { sinBanco.push({ o, clase: "DOBLE_REGISTRO", otra }); return; }
    const cob = cobertura[FUENTE_DE_METODO[o.metodo]];
    // Solo se juzga lo que el banco ya cubre completo: validada antes del ultimo dia cargado, y la orden creada
    // despues del primer dia cargado (si no, el pago pudo ser antes de lo que se tiene del banco).
    if (!cob || !cob.hasta || o.fecha_validacion >= cob.hasta || (o.fecha_creacion || "") < cob.desde) { porConfirmar += 1; return; }
    // Un pago en linea lo registra el sistema, no el plantel: si el banco no lo tiene, lo corrige Sistemas
    sinBanco.push({ o, clase: o.metodo === "LINEA" ? "EN_LINEA" : "VALIDADO_SIN_COBRO" });
  });

  // ------------------------------------------------------------ casos y resumen (solo el periodo)
  const casos = [];
  const nombreCajero = (o) => (o && o.id_cajero && cajeros[o.id_cajero] ? cajeros[o.id_cajero].nombre : (o && o.id_cajero) || "");
  const datosOrden = (o) => (o ? {
    orden: o.id, folio: o.folio_registro, estatus: o.estatus, metodo: ETIQUETAS_METODO[o.metodo] || "", capturado: o.folio_banco,
    montoOrden: o.monto, validada: o.fecha_validacion ? `${o.fecha_validacion} ${String(o.hora_validacion || "").substring(0, 5)}` : "",
    cajero: nombreCajero(o), matricula: String(o.codigo_alumno || "").trim(),
  } : { orden: "", folio: "", estatus: "", metodo: "", capturado: "", montoOrden: "", validada: "", cajero: "", matricula: "" });
  const datoBanco = (m) => (m.fuente === "TARJETA" ? m.autorizacion : m.folio);
  const cobroCorto = (m) => ({ clave: m.clave || "", medioCobro: etiquetaMedio(m.fuente, m.cuenta), fecha: m.fecha, hora: (m.hora || "").substring(0, 5), dato: datoBanco(m), importe: m.importe });
  const ordenCorta = (o) => ({ orden: o.id, folio: o.folio_registro, estatus: o.estatus, capturado: o.folio_banco, monto: o.monto, matricula: matriculaDe(o) });
  // Un cobro sin orden y una validacion sin cobro del mismo plantel e importe, con pocos dias de diferencia, suelen ser
  // el mismo pago con el dato mal capturado: a cada uno se le sugiere el otro. Solo si es del mismo alumno (cuando la
  // referencia lo dice) o del mismo medio de cobro (cuando no), y solo si hay una sola coincidencia: con importes
  // comunes ($1,125) hay muchas y la sugerencia confundiria.
  const sinCobro = sinBanco.filter((s) => s.clase === "VALIDADO_SIN_COBRO");
  const sueltos = resultados.filter((r) => r.clase === "SIN_ORDEN");
  const cercanos = (m, o) => {
    if (m.pl !== o.pl || !igual(m.importe, o.monto) || o.tsVal === null || o.tsVal - m.ts < -DIA || o.tsVal - m.ts > 15 * DIA) return false;
    const mat = datosDeReferencia(m.fuente, m.referencia, m.referencia_tecleada).matricula;
    return mat ? mat === matriculaDe(o) : FUENTE_DE_METODO[o.metodo] === m.fuente;
  };
  const unico = (lista) => (lista.length === 1 ? lista : []);

  resultados.filter((r) => r.clase !== "OK" && r.clase !== "PARTE" && enPeriodo(r.m.fecha)).forEach(({ m, clase, o, nota, probable, automatico, seConfirma, duplicadas, otras, partes, otroCobro }) => {
    // el caso es del plantel donde se cobro; si el banco no dice de que plantel es, del plantel de la orden
    const oficial = oficialDeBd(m.pl) || (o ? oficialDeBd(o.pl) : "");
    const deLaReferencia = datosDeReferencia(m.fuente, m.referencia, m.referencia_tecleada);
    const causa = clase === "DATO_MAL_CAPTURADO" && o && !/folio como autorización/.test(nota) ? causaDeCaptura(o, m) : "";
    // Cobro sin orden: ordenes pendientes del plantel por ese importe creadas cerca del cobro (para "Ligar" cuando la
    // referencia no dice el alumno) y validaciones que el banco no encuentra y pueden ser este mismo pago
    const posibles = clase === "SIN_ORDEN" && !deLaReferencia.matricula
      ? ords.filter((x) => libre(x) && x.estatus === "PENDIENTE" && x.pl === m.pl && igual(x.monto, m.importe) && x.folio_registro && x.tsCre !== null
        && m.ts - x.tsCre <= 3 * DIA && x.tsCre - m.ts <= DIA).slice(0, 5).map((x) => ({ ...ordenCorta(x), matricula: matriculaDe(x), creada: x.fecha_creacion }))
      : [];
    const sospechosas = clase === "SIN_ORDEN" ? unico(sinCobro.filter((s) => cercanos(m, s.o))).map((s) => ({ ...ordenCorta(s.o), metodo: ETIQUETA_DE_METODO[s.o.metodo] || "", validada: s.o.fecha_validacion })) : [];
    const extra = sospechosas.length
      ? `puede ser ${sospechosas.map((s) => `la orden ${s.orden} (validada el ${ddmm(s.validada)} como ${s.metodo.toLowerCase()} con «${s.capturado || "vacío"}»)`).join(" o ")}, que el banco no encuentra`
      : "";
    casos.push({
      oficial, plantel: nombresPlanteles[oficial] || "", tipo: clase, probable, fuente: m.fuente,
      fecha: m.fecha, hora: (m.hora || "").substring(0, 5), importe: m.importe,
      autorizacion: m.autorizacion, referencia: m.referencia, tecleado: m.referencia_tecleada, folioBanco: m.folio, medio: m.medio,
      clave: m.clave || "", cuenta: m.cuenta || "", automatico: !!automatico, seConfirma: !!seConfirma, duplicadas: duplicadas || [],
      medioCobro: etiquetaMedio(m.fuente, m.cuenta), deLaValidacion: false,
      matriculaBanco: deLaReferencia.matricula, conceptosBanco: deLaReferencia.conceptos,
      otra: clase === "POSIBLE_COBRO_DOBLE" && otroCobro ? { orden: o.id, ...cobroCorto(otroCobro) } : null,
      otras: (otras || []).map(ordenCorta), partes: (partes || []).map(cobroCorto), posibles, sospechosas,
      ...datosOrden(o), nota: [nota, causa, extra].filter(Boolean).join("; "),
      accion: accionDe(clase, m, o, datoBanco(m), { duplicadas, otras, partes, otroCobro: otroCobro ? cobroCorto(otroCobro) : null }),
    });
  });
  sinBanco.forEach(({ o, clase, otra }) => {
    const oficial = oficialDeBd(o.pl);
    // el cobro sin orden que puede ser el de esta validacion
    const suelto = clase === "VALIDADO_SIN_COBRO" ? unico(sueltos.filter((r) => cercanos(r.m, o)))[0] : null;
    const pista = suelto ? `el banco tiene un cobro sin orden de ${dinero(suelto.m.importe)} (${etiquetaMedio(suelto.m.fuente, suelto.m.cuenta).toLowerCase()} del ${ddmm(suelto.m.fecha)}${suelto.m.hora ? ` ${suelto.m.hora.substring(0, 5)}` : ""}, ${suelto.m.fuente === "TARJETA" ? "autorización" : "folio"} ${datoBanco(suelto.m)}): si es el de esta orden, usa «Encontré el ticket» con ese dato` : "";
    casos.push({
      oficial, plantel: nombresPlanteles[oficial] || "", tipo: clase, probable: false, fuente: FUENTE_DE_METODO[o.metodo],
      fecha: o.fecha_validacion, hora: String(o.hora_validacion || "").substring(0, 5), importe: o.monto,
      autorizacion: "", referencia: "", tecleado: "", folioBanco: "", medio: "", clave: "", cuenta: "", automatico: false, duplicadas: [],
      // estos casos salen de una validacion del plantel, no de un movimiento del banco
      medioCobro: ETIQUETA_DE_METODO[o.metodo] || "", deLaValidacion: true, matriculaBanco: "", conceptosBanco: [],
      otra: otra ? {
        orden: otra.o.id, metodo: ETIQUETA_DE_METODO[otra.o.metodo] || "", medioCobro: etiquetaMedio(otra.m.fuente, otra.m.cuenta),
        fecha: otra.m.fecha, hora: (otra.m.hora || "").substring(0, 5), dato: datoBanco(otra.m), importe: otra.m.importe,
      } : null,
      otras: [], partes: [], posibles: [], sospechosas: [],
      ...datosOrden(o),
      nota: pista ? `no hay ${ETIQUETAS_FUENTE[FUENTE_DE_METODO[o.metodo]].toLowerCase()} con ese dato en lo cargado del banco; ${pista}` : clase === "DOBLE_REGISTRO" ? `ese cobro (${etiquetaMedio(otra.m.fuente, otra.m.cuenta).toLowerCase()} del ${otra.m.fecha.substring(8, 10)}/${otra.m.fecha.substring(5, 7)}, ${otra.m.fuente === "TARJETA" ? "autorización" : "folio"} ${datoBanco(otra.m)}) ya está registrado en la orden ${otra.o.id}`
        : clase === "EN_LINEA" ? "quedó como pagado en línea y el banco no tiene ese cobro (puede ser un pago declinado)"
          : `no hay ${ETIQUETAS_FUENTE[FUENTE_DE_METODO[o.metodo]].toLowerCase()} con ese dato en lo cargado del banco`,
      accion: clase === "DOBLE_REGISTRO"
        ? `La orden ${o.id} se validó con el mismo cobro que ya está en la orden ${otra.o.id}. Si el alumno no hizo un segundo pago, cancelar esta orden.`
        : clase === "EN_LINEA" ? accionDe("EN_LINEA")
          : `Revisar el comprobante de la orden ${o.id}. Si el dato está mal, corregirlo; si no hubo cobro, deshacer la validación.`,
    });
  });
  const ordenTipos = Object.keys(TIPOS_CASO);
  casos.sort((a, b) => a.oficial.localeCompare(b.oficial) || ordenTipos.indexOf(a.tipo) - ordenTipos.indexOf(b.tipo) || a.fecha.localeCompare(b.fecha));

  // resumen por plantel: lo que dice el banco contra lo registrado en Multipagos
  const planteles = new Map();
  const fila = (oficial) => {
    if (!planteles.has(oficial)) {
      planteles.set(oficial, {
        oficial, nombre: nombresPlanteles[oficial] || (oficial ? "" : "Sin plantel identificado"),
        banco: { TARJETA: vacio(), BANORTE: vacio(), BBVA: vacio() }, cobrado: 0, comisiones: 0, depositado: 0,
        registrado: 0, registradoPorFuente: { TARJETA: 0, BANORTE: 0, BBVA: 0 }, porConfirmar: 0, casos: 0, porTipo: {},
      });
    }
    return planteles.get(oficial);
  };
  movs.filter((m) => enPeriodo(m.fecha) && m.tipo !== "COMISION" && m.cuenta !== AFILIACION_PINPAD).forEach((m) => {
    const f = fila(oficialDeBd(m.pl));
    const b = f.banco[m.fuente];
    b.movimientos += 1; b.cobrado += m.importe; b.comisiones += m.comision + m.iva; b.depositado += m.depositado;
    f.cobrado += m.importe; f.comisiones += m.comision + m.iva; f.depositado += m.depositado;
  });
  // Lo registrado se cuenta con la fecha del cobro (la del banco cuando se ligo, o la que anoto la validacion con el
  // banco) y solo hasta el ultimo dia cargado de cada banco, para comparar lo mismo contra lo mismo. Lo validado
  // despues de lo cargado queda "por confirmar" hasta que se suba el siguiente archivo.
  validadas.filter((o) => o.metodo && o.metodo !== "PINPAD").forEach((o) => {
    const ligado = usado.get(o.id);
    const fecha = (ligado && ligado.fecha) || String(o.fecha_pago || "").substring(0, 10) || o.fecha_validacion;
    if (!fecha || !enPeriodo(fecha)) return;
    const f = fila(oficialDeBd(o.pl));
    const cob = cobertura[FUENTE_DE_METODO[o.metodo]];
    if (ligado || (cob && cob.hasta && fecha <= cob.hasta)) {
      f.registrado += o.monto;
      f.registradoPorFuente[FUENTE_DE_METODO[o.metodo]] += o.monto;
    } else {
      f.porConfirmar += o.monto;
    }
  });
  casos.forEach((c) => { const f = fila(c.oficial); f.casos += 1; f.porTipo[c.tipo] = (f.porTipo[c.tipo] || 0) + 1; });
  const resumenPlanteles = [...planteles.values()].map((f) => ({
    ...f,
    cobrado: redondear(f.cobrado), comisiones: redondear(f.comisiones), depositado: redondear(f.depositado), registrado: redondear(f.registrado), porConfirmar: redondear(f.porConfirmar),
    diferencia: redondear(f.cobrado - f.registrado),
    banco: Object.fromEntries(Object.entries(f.banco).map(([k, v]) => [k, { movimientos: v.movimientos, cobrado: redondear(v.cobrado), comisiones: redondear(v.comisiones), depositado: redondear(v.depositado) }])),
    registradoPorFuente: Object.fromEntries(Object.entries(f.registradoPorFuente).map(([k, v]) => [k, redondear(v)])),
  })).sort((a, b) => (a.oficial || "99").localeCompare(b.oficial || "99"));

  const suma = (lista, campo) => redondear(lista.reduce((s, x) => s + x[campo], 0));
  const comisionesBbva = movs.filter((m) => m.tipo === "COMISION" && enPeriodo(m.fecha));
  const pinpadPeriodo = pinpad.filter((m) => enPeriodo(m.fecha));
  const okPeriodo = resultados.filter((r) => r.clase === "OK" && enPeriodo(r.m.fecha)).length;
  // Validacion con el banco: lo que coincide exactamente se valida; lo que ya estaba validado y cuadra se marca
  // como confirmado por el banco (fecha_pago). El servidor vuelve a revisar cada uno.
  const validarConBanco = {
    validar: casos.filter((c) => c.automatico).map((c) => ({ clave: c.clave, orden: c.orden, importe: c.importe, oficial: c.oficial, fuente: c.fuente })),
    // mismas reglas que el servidor: lo capturado es el folio o la autorizacion del banco, mismo importe y plantel
    confirmar: resultados.filter((r) => r.clase === "OK" && r.o && !r.o.confirmada && r.m.clave && enPeriodo(r.m.fecha)
      && r.o.folio_banco.toUpperCase() === (r.m.fuente === "TARJETA" ? r.m.autorizacion : r.m.folio).toUpperCase()
      && igual(r.o.monto, r.m.importe) && r.o.pl === r.m.pl)
      .map((r) => ({ clave: r.m.clave, orden: r.o.id }))
      // la orden pagada en varias partes: el servidor suma las partes y revisa alumno y plantel de cada una
      .concat(resultados.filter((r) => r.clase === "VARIOS_COBROS_UNA_ORDEN" && r.seConfirma && !r.o.confirmada && enPeriodo(r.m.fecha))
        .map((r) => ({ clave: r.m.clave, orden: r.o.id, partes: r.partes.map((p) => p.clave) }))),
  };
  return {
    planteles: resumenPlanteles,
    casos,
    validarConBanco,
    totales: {
      cobrado: suma(resumenPlanteles, "cobrado"), comisiones: suma(resumenPlanteles, "comisiones"), depositado: suma(resumenPlanteles, "depositado"),
      registrado: suma(resumenPlanteles, "registrado"), porConfirmarImporte: suma(resumenPlanteles, "porConfirmar"), casos: casos.length, ok: okPeriodo, porConfirmar,
      comisionesBbva: redondear(comisionesBbva.reduce((s, m) => s + m.comision + m.iva, 0)),
      pinpad: { movimientos: pinpadPeriodo.length, importe: suma(pinpadPeriodo, "importe") },
      devoluciones: devoluciones.filter((m) => enPeriodo(m.fecha)).length,
    },
  };
}

const vacio = () => ({ movimientos: 0, cobrado: 0, comisiones: 0, depositado: 0 });

// Lo que el plantel tiene que hacer con cada caso (se muestra a Financieros y, en la siguiente entrega, al plantel)
function accionDe(clase, m = {}, o = null, dato = "", extra = {}) {
  if (clase === "EN_LINEA") return "Lo corrige Sistemas desde Conciliación de pagos en línea.";
  const fuente = m.fuente === "TARJETA" ? (m.cuenta === AFILIACION_EN_LINEA ? "pago en línea" : "TPV") : ETIQUETAS_FUENTE[m.fuente].toLowerCase();
  const id = o ? o.id : "";
  const queDato = m.fuente === "TARJETA" ? "autorización" : "folio";
  switch (clase) {
    case "COBRADO_SIN_VALIDAR": {
      const gemelas = (extra.duplicadas || []).filter((x) => String(x) !== String(id));
      const base = `Validar la orden ${id} con ${fuente} (${queDato} ${dato}).`;
      return gemelas.length ? `${base} La ficha se generó ${gemelas.length + 1} veces (también la${gemelas.length > 1 ? "s" : ""} orden${gemelas.length > 1 ? "es" : ""} ${gemelas.join(", ")}): valida solo esta; ${gemelas.length > 1 ? "las demás quedan" : "la otra queda"} pendiente${gemelas.length > 1 ? "s" : ""} y no se debe${gemelas.length > 1 ? "n" : ""} cobrar otra vez.` : base;
    }
    case "UN_COBRO_VARIAS_ORDENES": {
      const lista = [o, ...(extra.otras || [])].map((x) => `${x.id} (${dinero(x.monto)})`).join(", ");
      return `Un solo cobro de ${fuente} por ${dinero(m.importe)} (${queDato} ${dato}) pagó ${(extra.otras || []).length + 1} órdenes del alumno: ${lista}. No deshagas ninguna validación ni generes otra orden: responde confirmando con el ticket.`;
    }
    case "FOLIO_DE_OTRO_ALUMNO": {
      const otra = (extra.otras || [])[0] || {};
      return `El cobro de TPV (${queDato} ${dato}) se validó en la orden ${id}, pero en la terminal se tecleó el folio de la orden ${otra.id} de otro alumno (matrícula ${String(otra.codigo_alumno || "").trim()}), que sigue pendiente. Revisa el ticket con los alumnos: si el pago es del otro alumno, usa «Reasignar»; si solo se tecleó mal el folio, usa «Responder».`;
    }
    case "VARIOS_COBROS_UNA_ORDEN": {
      const lista = [m, ...(extra.partes || [])].map((p) => `${dinero(p.importe)} del ${ddmm(p.fecha)} (${p.fuente === "TARJETA" ? "autorización" : "folio"} ${p.fuente === "TARJETA" ? p.autorizacion : p.folio})`).join(" y ");
      return `La orden ${id} (${dinero(o.monto)}) se pagó en ${(extra.partes || []).length + 1} partes: ${lista}. No generes otra orden: responde confirmando con los comprobantes del alumno.`;
    }
    case "SIN_ORDEN": return `Generar la orden del alumno y validarla con ${fuente} (${m.fuente === "TARJETA" ? "autorización" : "folio"} ${dato}).`;
    case "DATO_MAL_CAPTURADO": return `Corregir la autorización de la orden ${id}: dice ${o.folio_banco || "(vacío)"} y la del banco es ${dato}.`;
    case "METODO_EQUIVOCADO": return `Cambiar el método de la orden ${id} a ${fuente} (${m.fuente === "TARJETA" ? "autorización" : "folio"} ${dato}).`;
    case "BANCO_EQUIVOCADO": return `Cambiar el banco de la orden ${id} a ${fuente} (folio ${dato}).`;
    case "MONTO_DISTINTO": return `Revisar la orden ${id}: el banco cobró ${dinero(m.importe)} y la orden es por ${dinero(o.monto)}.`;
    case "OTRO_PLANTEL": return `El cobro es de este plantel y se validó en la orden ${id}, de otro plantel. Revisar con ese plantel.`;
    case "POSIBLE_COBRO_DOBLE": {
      const otro = extra.otroCobro;
      if (!otro) return `Revisar con el alumno: la orden ${id} ya tiene otro cobro y este puede ser un cobro doble.`;
      return `Pregunta al alumno si pagó dos veces la orden ${id}: el otro cobro es ${otro.medioCobro.toLowerCase()} del ${ddmm(otro.fecha)}${otro.hora ? ` ${otro.hora}` : ""} (${otro.medioCobro === "Ventanilla Banorte" || otro.medioCobro === "Ventanilla BBVA" ? "folio" : "autorización"} ${otro.dato}). Si sí, responde para que Financieros tramite la devolución; si este pago era de otra orden, dilo en la respuesta.`;
    }
    case "ORDEN_CANCELADA": return `El alumno pagó la orden ${id}, que está cancelada. Revisar.`;
    case "COBRO_DEVUELTO": return `El banco devolvió el cobro de la orden ${id}. Si el alumno no volvió a pagar, deshacer la validación.`;
    case "EN_LINEA": return "Lo corrige Sistemas desde Conciliación de pagos en línea.";
    default: return "";
  }
}

export { oficialDeBd, bdDeOficial };
